/**
 * SubmissionVersendenDialog — pick the neu Offertanfragen + Frist, send them, show the per-row
 * result (P2 Placement Slice 8). Rows without an e-mail are disabled + hinted.
 */

import { useMemo, useState } from 'react';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import {
  SUBMISSION_KEINE_EMAIL_HINWEIS,
  SUBMISSION_MAILBOX_HINWEIS,
} from '@brokly/shared';
import type {
  SubmissionEintrag,
  SubmissionVersandErgebnis,
  SubmissionVersandFehlerCode,
  SubmissionVersendenResponse,
} from '@brokly/shared';

const FEHLER_TEXT: Record<SubmissionVersandFehlerCode, string> = {
  carrier_email_missing: SUBMISSION_KEINE_EMAIL_HINWEIS,
  mailbox_not_connected: SUBMISSION_MAILBOX_HINWEIS,
  mailbox_reauth_required: SUBMISSION_MAILBOX_HINWEIS,
  mail_send_blocked: 'Versand durch die Mail-Richtlinie blockiert.',
  mailbox_send_failed: 'Postfach-Provider hat den Versand abgelehnt.',
  attachment_too_large: 'Beilagen zu gross.',
  versand_laeuft: 'Versand läuft bereits.',
  versand_unterbrochen: 'Versand unterbrochen - bitte zuerst auflösen.',
  status_not_neu: 'Status nicht mehr neu.',
  unbekannt: 'Versand fehlgeschlagen.',
};

export function SubmissionVersendenDialog({
  open,
  onClose,
  neuItems,
  fristDefault,
  mailboxVerbunden,
  pending,
  onVersenden,
}: {
  open: boolean;
  onClose: () => void;
  neuItems: SubmissionEintrag[];
  fristDefault: string;
  mailboxVerbunden: boolean;
  pending: boolean;
  onVersenden: (submissionIds: string[], fristDate: string) => Promise<SubmissionVersendenResponse | undefined>;
}) {
  const sendable = useMemo(() => neuItems.filter((i) => i.submission_faehig).map((i) => i.id), [neuItems]);
  const [checked, setChecked] = useState<Set<string>>(() => new Set(sendable));
  const [frist, setFrist] = useState(fristDefault);
  const [result, setResult] = useState<SubmissionVersendenResponse | null>(null);

  const toggle = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function doSend() {
    const r = await onVersenden([...checked], frist);
    if (r) setResult(r);
  }

  function row(ergebnis: SubmissionVersandErgebnis, kind: 'ok' | 'fail') {
    return (
      <li key={`${kind}-${ergebnis.submission_id}`} className={kind === 'fail' ? 'text-status-danger' : ''}>
        <span className="mono-num">{ergebnis.submission_number}</span>{' '}
        {kind === 'fail' && ergebnis.code ? FEHLER_TEXT[ergebnis.code] : 'versandt'}
      </li>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Offertanfragen versenden"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Schliessen
          </Button>
          <Button disabled={pending || checked.size === 0} onClick={() => void doSend()} data-testid="sub-versenden-submit">
            {pending ? 'Wird versandt …' : `Versenden (${checked.size})`}
          </Button>
        </div>
      }
    >
      <div data-testid="sub-versenden-dialog" className="flex flex-col gap-3 text-sm">
        {!mailboxVerbunden && (
          <p className="rounded-md border border-status-warning-border bg-status-warning-bg p-2 text-xs text-status-warning" role="note">
            {SUBMISSION_MAILBOX_HINWEIS}
          </p>
        )}
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">Antwortfrist</span>
          <input
            type="date"
            className="rounded-md border border-border bg-background p-1"
            value={frist}
            onChange={(e) => setFrist(e.target.value)}
            data-testid="sub-versenden-frist"
          />
          <span className="text-xs text-muted-foreground">(Default: 10 Arbeitstage)</span>
        </label>
        <ul className="flex flex-col gap-1">
          {neuItems.map((it) => (
            <li key={it.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={checked.has(it.id)}
                disabled={!it.submission_faehig}
                onChange={() => toggle(it.id)}
                data-testid={`sub-versenden-check-${it.carrier_uid}`}
              />
              <span>{it.name ?? it.carrier_uid}</span>
              <span className="mono-num text-xs text-muted-foreground">{it.submission_number}</span>
              {!it.submission_faehig && <span className="text-xs text-status-warning">{SUBMISSION_KEINE_EMAIL_HINWEIS}</span>}
            </li>
          ))}
        </ul>
        {result && (
          <div className="flex flex-col gap-1 border-t border-border pt-2" data-testid="sub-versenden-result">
            {result.versendet.length > 0 && (
              <p className="text-status-success">{result.versendet.length} versandt</p>
            )}
            {result.versendet.length > 0 && <ul>{result.versendet.map((e) => row(e, 'ok'))}</ul>}
            {result.fehlgeschlagen.length > 0 && <ul>{result.fehlgeschlagen.map((e) => row(e, 'fail'))}</ul>}
            {result.hinweise.map((h, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                {h}
              </p>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}
